<template>
  <div class="faculty-timetable-view">
    <div class="rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-700 shadow-xl p-8 text-white relative overflow-hidden mb-8">
      <div class="absolute inset-0 bg-black opacity-5" aria-hidden="true" />
      <div class="relative z-10">
        <h1 class="text-2xl md:text-3xl font-bold flex items-center gap-3">
          <font-awesome-icon icon="calendar-week" class="opacity-90" />
          Timetable
        </h1>
        <p class="text-blue-100 mt-3 max-w-3xl leading-relaxed">
          View your weekly teaching sessions, times, and room assignments in one place.
        </p>
      </div>
    </div>

    <!-- Live clock & session awareness -->
    <div class="mb-8 ftv-insight-row flex flex-col gap-4">
      <div class="ftv-clock-slot">
        <local-time-clock :sync-date="clockNow" />
      </div>

      <div class="ftv-insight-main space-y-4">
        <div v-if="!scheduleInsights.hasEntries"
          class="ftv-insight-placeholder">
          <div v-if="loadingInsightPlaceholder" class="py-4">
            <base-page-spinner type="spin" class-list="h-10 mx-auto" />
            <p class="text-sm text-slate-600 font-medium mt-4">Preparing session alerts…</p>
          </div>
          <template v-else>
            <font-awesome-icon icon="calendar-alt" class="text-slate-300 text-3xl mb-3" />
            <p class="text-sm text-slate-600 font-medium">Session alerts will appear here</p>
            <p class="text-xs text-slate-500 mt-2 ftv-max-w-copy mx-auto leading-relaxed">
              Once your time table is loaded, you will see where you should be teaching now and what is coming next.
            </p>
          </template>
        </div>

        <template v-else>
          <!-- Current session -->
          <div v-if="scheduleInsights.current"
            class="ftv-card-now">
            <div class="ftv-card-pad flex flex-col sm:flex-row sm:items-start ftv-gap-inner">
              <div class="ftv-icon-badge ftv-icon-badge-emerald">
                <font-awesome-icon icon="chalkboard-teacher" class="text-lg" />
              </div>
              <div class="ftv-flex-min">
                <div class="ftv-badge-row">
                  <span class="ftv-badge ftv-badge-now">
                    Now
                  </span>
                  <span class="ftv-now-meta">{{ scheduleInsights.current.timeLabel }}</span>
                </div>
                <p class="text-lg sm:text-xl font-bold text-slate-900 leading-snug">
                  {{ scheduleInsights.current.headline }}
                </p>
                <p class="text-sm text-slate-600 mt-2 leading-relaxed">
                  {{ scheduleInsights.current.detail }}
                </p>
                <p v-if="scheduleInsights.current.untilLabel" class="text-xs font-semibold text-emerald-700 mt-3">
                  {{ scheduleInsights.current.untilLabel }}
                </p>
              </div>
            </div>
          </div>

          <div v-else
            class="ftv-card-between">
            <div class="ftv-icon-badge ftv-icon-badge-muted">
              <font-awesome-icon icon="clock" class="text-lg" />
            </div>
            <div>
              <p class="text-sm font-semibold text-slate-800">Not in a scheduled teaching period</p>
              <p class="text-sm text-slate-600 mt-1 leading-relaxed">{{ scheduleInsights.betweenSessionsHint }}</p>
            </div>
          </div>

          <!-- Next session -->
          <div v-if="scheduleInsights.next"
            class="ftv-card-next">
            <div class="ftv-card-pad flex flex-col sm:flex-row sm:items-start ftv-gap-inner">
              <div class="ftv-icon-badge ftv-icon-badge-indigo">
                <font-awesome-icon icon="arrow-right" class="text-lg" />
              </div>
              <div class="ftv-flex-min">
                <div class="ftv-badge-row">
                  <span class="ftv-badge ftv-badge-next">
                    Next
                  </span>
                  <span class="ftv-next-meta">{{ scheduleInsights.next.whenLabel }}</span>
                </div>
                <p class="text-lg sm:text-xl font-bold text-slate-900 leading-snug">
                  {{ scheduleInsights.next.headline }}
                </p>
                <p class="text-sm text-slate-600 mt-2 leading-relaxed">
                  {{ scheduleInsights.next.detail }}
                </p>
                <p v-if="scheduleInsights.next.countdownLabel" class="text-xs font-semibold text-indigo-700 mt-3">
                  {{ scheduleInsights.next.countdownLabel }}
                </p>
              </div>
            </div>
          </div>

          <div v-else-if="scheduleInsights.hasEntries"
            class="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-600">
            <font-awesome-icon icon="calendar-check" class="mr-2 text-slate-400" />
            No further sessions in your timetable after this point in the week.
          </div>
        </template>
      </div>
    </div>

    <div v-if="loadingSemesters" class="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
      <base-page-spinner type="spin" class-list="h-12 mx-auto" />
      <p class="text-gray-500 mt-4 text-sm">Loading your timetable…</p>
    </div>

    <div v-else-if="semestersError" class="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-amber-900 text-sm">
      {{ semestersError }}
    </div>

    <div v-else-if="!facultyLinked" class="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
      <div class="flex gap-4 items-start">
        <font-awesome-icon icon="user-slash" class="text-2xl text-amber-600 ftv-icon-stack" />
        <div>
          <h2 class="text-lg font-semibold text-gray-900">Faculty profile not linked</h2>
          <p class="text-gray-600 mt-2 leading-relaxed">{{ semestersMessage }}</p>
        </div>
      </div>
    </div>

    <div v-else-if="facultyLinked && semesters.length === 0" class="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
      <div class="flex gap-4 items-start">
        <font-awesome-icon icon="calendar-times" class="text-2xl text-gray-400 ftv-icon-stack" />
        <div>
          <h2 class="text-lg font-semibold text-gray-900">No time tables</h2>
          <p class="text-gray-600 mt-2 leading-relaxed">{{ semestersMessage }}</p>
        </div>
      </div>
    </div>

    <div v-else class="space-y-6">
      <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div class="px-4 sm:px-6 py-4 border-b border-gray-100 bg-slate-50">
          <h2 class="text-sm font-semibold text-gray-800">Your time tables</h2>
          <p class="text-xs text-gray-500 mt-1">Select a time table to view your schedule.</p>
        </div>
        <div class="p-4 sm:p-6 flex flex-wrap gap-2">
          <button v-for="sem in semesters" :key="sem.program_semester_id" type="button"
            @click="selectSemester(sem)"
            :class="[
              'ftv-sem-btn px-4 rounded-xl text-sm font-medium border transition-all text-left max-w-full',
              selectedProgramSemesterId === sem.program_semester_id
                ? 'ftv-sem-btn-active border-blue-600 bg-blue-50 text-blue-900'
                : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50'
            ]">
            <span class="block font-semibold leading-snug">{{ sem.semester_name }}</span>
            <span v-if="sem.program_name" class="block text-xs text-gray-500 ftv-mt-tight">{{ sem.program_name }}</span>
            <span class="inline-flex mt-2 text-xs px-2 ftv-pill-y rounded-full"
              :class="sem.has_generated_slots ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'">
              {{ sem.has_generated_slots ? sem.slot_count + ' session' + (sem.slot_count === 1 ? '' : 's') : 'No timetable yet' }}
            </span>
          </button>
        </div>
      </div>

      <div v-if="selectedSemester && !selectedSemester.has_generated_slots"
        class="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
        <div class="flex gap-4 items-start">
          <font-awesome-icon icon="calendar-plus" class="text-2xl text-blue-400 ftv-icon-stack" />
          <div>
            <h2 class="text-lg font-semibold text-gray-900">No timetable for this time table</h2>
            <p class="text-gray-600 mt-2 leading-relaxed">
              You are assigned to this time table, but no generated timetable slots include your teaching yet.
              Check back after an administrator generates the timetable.
            </p>
          </div>
        </div>
      </div>

      <div v-else-if="selectedSemester && selectedSemester.has_generated_slots">
        <div v-if="loadingDetail" class="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
          <base-page-spinner type="spin" class-list="h-10 mx-auto" />
          <p class="text-gray-500 mt-3 text-sm">Loading schedule…</p>
        </div>
        <div v-else-if="detailError" class="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-800 text-sm">
          {{ detailError }}
        </div>
        <div v-else-if="!timetableEntriesSorted.length" class="bg-white rounded-2xl border border-gray-100 p-8 text-center text-gray-600">
          No entries returned for this time table.
        </div>
        <div v-else class="space-y-4">
          <p class="text-sm text-gray-600">
            Weekly grid: all days (Monday to Sunday) and hourly slots for the working window; cells are blank where you have no session. Each cell lists sessions that overlap that hour.
          </p>
          <div v-if="generatedTimetableGridTimeSlots.length === 0"
            class="text-center py-8 bg-amber-50 rounded-xl border border-amber-100 text-sm text-amber-900">
            Could not build time rows from this data.
          </div>
          <div v-else class="overflow-x-auto rounded-xl border-2 border-gray-200 shadow-sm bg-white">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gradient-to-r from-blue-600 to-blue-700">
                <tr>
                  <th
                    class="sticky left-0 z-20 px-4 py-3 text-left text-sm font-bold text-white uppercase border-r ftv-th-border bg-blue-600 ftv-min-w-time ftv-th-sticky-shadow">
                    <div class="flex items-center">
                      <font-awesome-icon icon="clock" class="mr-2 opacity-90" />
                      Time
                    </div>
                  </th>
                  <th v-for="day in generatedTimetableGridDays" :key="day"
                    class="px-3 py-3 text-center text-sm font-bold text-white uppercase border-r ftv-th-border ftv-th-day"
                    :class="{ 'ftv-th-today': day === timetableTodayWeekday }">
                    {{ day }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(slot, slotIdx) in generatedTimetableGridTimeSlots" :key="slot.key"
                  :class="[
                    slotIdx % 2 === 0 ? 'bg-white' : 'ftv-row-alt',
                    isTimetableGridTimeSlotNow(slot) && 'ftv-tr-now'
                  ]">
                  <td
                    class="sticky left-0 z-10 px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 border-r border-gray-200 ftv-min-w-time ftv-td-sticky-shadow"
                    :class="[
                      slotIdx % 2 === 0 ? 'bg-gray-50' : 'ftv-td-time-alt',
                      isTimetableGridTimeSlotNow(slot) && 'ftv-td-time-now'
                    ]">
                    {{ slot.label }}
                  </td>
                  <td v-for="day in generatedTimetableGridDays" :key="day + '-' + slot.key"
                    class="px-3 py-3 align-top border-r border-gray-200 ftv-grid-cell"
                    :class="{
                      'ftv-grid-cell-today': day === timetableTodayWeekday,
                      'ftv-grid-cell-now': day === timetableTodayWeekday && isTimetableGridTimeSlotNow(slot)
                    }">
                    <template v-for="entries in [getGeneratedTimetableGridEntries(day, slot.start, slot.end)]"
                      :key="'cell-' + day + '-' + slot.key + '-' + (entries.length ? entries.map(function (e) { return e.id; }).join('x') : '_')">
                      <div v-for="entry in entries" :key="entry.id"
                        class="ftv-grid-entry rounded-lg transition-colors shadow-sm">
                        <div class="text-xs font-semibold ftv-text-blue-dark mb-1 leading-snug">
                          <font-awesome-icon icon="book" class="mr-1 text-blue-700" />
                          {{ entry.course && entry.course.name ? entry.course.name : 'N/A' }}
                        </div>
                        <div v-if="entry.room && entry.room.title" class="text-xs text-gray-600 flex items-start gap-1">
                          <font-awesome-icon icon="door-open" class="ftv-icon-tight-top text-gray-500 ftv-shrink-0" />
                          <span>{{ entry.room.title }}</span>
                        </div>
                        <div class="ftv-entry-time font-mono text-gray-500 pt-1 border-t border-blue-100">
                          {{ formatTimetableEntryTimeRange(entry) }}
                        </div>
                      </div>
                      <span v-if="entries.length === 0" class="text-gray-300 text-sm select-none">—</span>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <details class="rounded-xl border border-gray-200 ftv-details-bg px-4 py-3 text-sm">
            <summary class="cursor-pointer font-semibold text-gray-800 select-none">List view</summary>
            <div class="mt-4 overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-slate-800">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wide">Day</th>
                    <th class="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wide">Time</th>
                    <th class="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wide">Course</th>
                    <th class="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wide">Room</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-100">
                  <tr v-for="entry in timetableEntriesSorted" :key="'list-' + entry.id" class="ftv-list-hover">
                    <td class="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{{ entry.day }}</td>
                    <td class="px-4 py-3 text-sm text-gray-800 whitespace-nowrap font-mono">{{ formatTimetableEntryTimeRange(entry) }}</td>
                    <td class="px-4 py-3 text-sm text-gray-800">{{ entry.course && entry.course.name ? entry.course.name : '—' }}</td>
                    <td class="px-4 py-3 text-sm text-gray-800">{{ entry.room && entry.room.title ? entry.room.title : '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { gql } from 'graphql-tag';
import LocalTimeClock from '../../../components/shared/LocalTimeClock.vue';

export default {
  name: 'FacultyTimetableView',
  components: {
    LocalTimeClock
  },
  data() {
    return {
      loadingSemesters: true,
      semestersError: '',
      facultyLinked: false,
      semestersMessage: '',
      semesters: [],
      selectedProgramSemesterId: null,
      loadingDetail: false,
      detailError: '',
      detailEntries: [],
      clockNow: new Date(),
      clockTimerId: null
    };
  },
  created() {
    var self = this;
    this.clockTimerId = setInterval(function () {
      self.clockNow = new Date();
    }, 1000);
  },
  beforeUnmount() {
    if (this.clockTimerId) {
      clearInterval(this.clockTimerId);
      this.clockTimerId = null;
    }
  },
  computed: {
    loadingInsightPlaceholder() {
      return !!(this.loadingDetail && this.selectedSemester && this.selectedSemester.has_generated_slots);
    },
    scheduleInsights() {
      var entries = this.timetableEntriesSorted;
      if (!entries || !entries.length) {
        return {
          hasEntries: false,
          current: null,
          next: null,
          betweenSessionsHint: ''
        };
      }
      var now = this.clockNow;
      var todayName = now.toLocaleDateString('en-US', { weekday: 'long' });
      var nowM = now.getHours() * 60 + now.getMinutes();
      var order = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      var todayIdx = order.indexOf(todayName);
      if (todayIdx < 0) {
        todayIdx = 0;
      }
      var nowWeekMinutes = todayIdx * 1440 + nowM;

      var self = this;
      var current = null;
      var currentEntry = null;
      for (var ci = 0; ci < entries.length; ci++) {
        var e = entries[ci];
        if (e.day !== todayName) {
          continue;
        }
        var sm = self.parseTimeToMinutes(e.start_time);
        var em = self.parseTimeToMinutes(e.end_time);
        if (sm < 0 || em < 0) {
          continue;
        }
        if (nowM >= sm && nowM < em) {
          var courseName = e.course && e.course.name ? e.course.name : 'Session';
          var roomLabel = e.room && e.room.title ? e.room.title : 'TBC';
          var startLbl = self.formatTime12hFromMinutes(sm);
          var endLbl = self.formatTime12hFromMinutes(em);
          var endsIn = em - nowM;
          current = {
            timeLabel: startLbl + ' – ' + endLbl,
            headline: 'You should be in ' + roomLabel + ' teaching ' + courseName,
            detail: 'It is now ' + self.formatTime12hFromMinutes(nowM) + '. This period runs from ' + startLbl + ' to ' + endLbl + '.',
            untilLabel: endsIn > 0 ? 'Ends in ' + self.formatDurationShort(endsIn) : ''
          };
          currentEntry = e;
          break;
        }
      }

      var searchFromWeek = nowWeekMinutes;
      if (currentEntry) {
        var endCur = self.parseTimeToMinutes(currentEntry.end_time);
        if (endCur >= 0) {
          searchFromWeek = todayIdx * 1440 + endCur;
        }
      }

      var strictGreater = currentEntry === null;
      var nextMeta = self.findNextSessionMeta(entries, searchFromWeek, order, todayName, strictGreater, nowWeekMinutes);
      var next = null;
      if (nextMeta && nextMeta.entry) {
        var ne = nextMeta.entry;
        var nsm = self.parseTimeToMinutes(ne.start_time);
        var nem = self.parseTimeToMinutes(ne.end_time);
        var ncourse = ne.course && ne.course.name ? ne.course.name : 'Session';
        var nroom = ne.room && ne.room.title ? ne.room.title : 'TBC';
        var startStr = self.formatTime12hFromMinutes(nsm);
        var endStr = nem >= 0 ? self.formatTime12hFromMinutes(nem) : '';
        var whenLabel = nextMeta.whenLabel;
        var deltaMin = nextMeta.deltaMinutes;
        next = {
          whenLabel: whenLabel,
          headline: nroom + ' — ' + ncourse,
          detail: endStr
            ? 'Teaching ' + ncourse + ' in ' + nroom + ', from ' + startStr + ' to ' + endStr + '.'
            : 'Teaching ' + ncourse + ' in ' + nroom + ', starting at ' + startStr + '.',
          countdownLabel: deltaMin > 0 ? 'Starts in ' + self.formatDurationLong(deltaMin) : ''
        };
      }

      var betweenHint = 'You are not in a scheduled teaching slot right now. Use this time to prepare, travel between rooms, or take a break.';
      if (!current && next) {
        betweenHint = 'Next session: ' + next.whenLabel + '. ' + betweenHint;
      }

      return {
        hasEntries: true,
        current: current,
        next: next,
        betweenSessionsHint: betweenHint
      };
    },
    selectedSemester() {
      var id = this.selectedProgramSemesterId;
      if (!id || !this.semesters.length) {
        return null;
      }
      for (var i = 0; i < this.semesters.length; i++) {
        if (String(this.semesters[i].program_semester_id) === String(id)) {
          return this.semesters[i];
        }
      }
      return null;
    },
    timetableEntriesSorted() {
      var order = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7 };
      var raw = this.detailEntries;
      if (!raw || !raw.length) {
        return [];
      }
      var list = raw.slice();
      list.sort(function (a, b) {
        var da = order[a.day] || 99;
        var db = order[b.day] || 99;
        if (da !== db) {
          return da - db;
        }
        var sa = a.start_time ? String(a.start_time) : '';
        var sb = b.start_time ? String(b.start_time) : '';
        if (sa < sb) {
          return -1;
        }
        if (sa > sb) {
          return 1;
        }
        return 0;
      });
      return list;
    },
    generatedTimetableGridDays() {
      return [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
      ];
    },
    generatedTimetableGridTimeSlots() {
      var self = this;
      var entries = this.timetableEntriesSorted;
      if (!entries || !entries.length) {
        return [];
      }
      var STEP = 60;
      var DEFAULT_START_MIN = 8 * 60;
      var DEFAULT_END_MIN = 18 * 60;
      var ABS_MIN = 6 * 60;
      var ABS_MAX = 24 * 60;

      var minM = -1;
      var maxM = -1;
      var ei;
      for (ei = 0; ei < entries.length; ei++) {
        var e = entries[ei];
        var sm = self.parseTimeToMinutes(e.start_time);
        var em = self.parseTimeToMinutes(e.end_time);
        if (sm >= 0) {
          minM = minM < 0 ? sm : Math.min(minM, sm);
        }
        if (em >= 0) {
          maxM = maxM < 0 ? em : Math.max(maxM, em);
        }
      }
      if (minM < 0 || maxM < 0) {
        return [];
      }

      var gridStart = Math.max(
        ABS_MIN,
        Math.min(DEFAULT_START_MIN, Math.floor(minM / STEP) * STEP - STEP)
      );
      var gridEnd = Math.min(
        ABS_MAX,
        Math.max(DEFAULT_END_MIN, Math.ceil(maxM / STEP) * STEP + STEP)
      );
      if (gridStart >= gridEnd) {
        return [];
      }

      var slots = [];
      var t;
      for (t = gridStart; t + STEP <= gridEnd; t += STEP) {
        var startStr = self.minutesToTimetableHms(t);
        var endStr = self.minutesToTimetableHms(t + STEP);
        slots.push({
          key: startStr + '|' + endStr,
          start: startStr,
          end: endStr,
          label: self.formatTimetableEntryTimeRange({ start_time: startStr, end_time: endStr })
        });
      }
      return slots;
    },
    timetableTodayWeekday() {
      return this.clockNow.toLocaleDateString('en-US', { weekday: 'long' });
    }
  },
  mounted() {
    this.loadSemesters();
  },
  methods: {
    async loadSemesters() {
      this.loadingSemesters = true;
      this.semestersError = '';
      try {
        var response = await this.$apollo.query({
          query: gql`
            query FacultyTimetableSemesters {
              facultyTimetableSemesters {
                error
                error_code
                message
                faculty_linked
                faculty_id
                semesters {
                  program_semester_id
                  semester_name
                  program_name
                  has_generated_slots
                  slot_count
                }
              }
            }
          `,
          fetchPolicy: 'network-only'
        });
        var wrap = response.data && response.data.facultyTimetableSemesters;
        if (!wrap) {
          this.semestersError = 'Unexpected response.';
          return;
        }
        if (wrap.error) {
          this.semestersError = wrap.message ? String(wrap.message) : 'Could not load timetables.';
          return;
        }
        this.facultyLinked = !!wrap.faculty_linked;
        this.semestersMessage = wrap.message ? String(wrap.message) : '';
        this.semesters = wrap.semesters || [];
        if (this.semesters.length) {
          var pick = null;
          for (var i = 0; i < this.semesters.length; i++) {
            if (this.semesters[i].has_generated_slots) {
              pick = this.semesters[i];
              break;
            }
          }
          if (pick) {
            this.selectedProgramSemesterId = pick.program_semester_id;
            this.loadDetail();
          } else {
            this.selectedProgramSemesterId = this.semesters[0].program_semester_id;
          }
        }
      } catch (e) {
        this.semestersError = e && e.message ? String(e.message) : 'Could not load timetables.';
      } finally {
        this.loadingSemesters = false;
      }
    },
    selectSemester(sem) {
      this.selectedProgramSemesterId = sem.program_semester_id;
      this.detailEntries = [];
      this.detailError = '';
      if (sem.has_generated_slots) {
        this.loadDetail();
      }
    },
    async loadDetail() {
      var sid = this.selectedProgramSemesterId;
      if (!sid) {
        return;
      }
      this.loadingDetail = true;
      this.detailError = '';
      this.detailEntries = [];
      try {
        var response = await this.$apollo.query({
          query: gql`
            query MyFacultyTimetable($program_semester_id: ID!) {
              myFacultyTimetable(program_semester_id: $program_semester_id) {
                error
                message
                data {
                  id
                  day
                  start_time
                  end_time
                  course { id name }
                  room { id title }
                }
              }
            }
          `,
          variables: { program_semester_id: String(sid) },
          fetchPolicy: 'network-only'
        });
        var wrap = response.data && response.data.myFacultyTimetable;
        if (!wrap || wrap.error) {
          this.detailError = wrap && wrap.message ? String(wrap.message) : 'Could not load this timetable.';
          return;
        }
        this.detailEntries = wrap.data || [];
      } catch (e) {
        this.detailError = e && e.message ? String(e.message) : 'Could not load this timetable.';
      } finally {
        this.loadingDetail = false;
      }
    },
    formatTimetableEntryTimeRange(entry) {
      var a = entry && entry.start_time ? String(entry.start_time) : '';
      var b = entry && entry.end_time ? String(entry.end_time) : '';
      var sm = this.parseTimeToMinutes(a);
      var em = this.parseTimeToMinutes(b);
      if (sm >= 0 && em >= 0) {
        return this.formatTime12hFromMinutes(sm) + ' – ' + this.formatTime12hFromMinutes(em);
      }
      function short(t) {
        if (!t) {
          return '';
        }
        var parts = t.split(':');
        if (parts.length >= 2) {
          return parts[0] + ':' + parts[1];
        }
        return t;
      }
      return short(a) + ' – ' + short(b);
    },
    normalizeTimetableTimeForGrid(t) {
      if (!t) {
        return '00:00:00';
      }
      var s = String(t).trim();
      if (s.length === 5 && s.indexOf(':') === 2) {
        return s + ':00';
      }
      return s;
    },
    getGeneratedTimetableGridEntries(day, slotStart, slotEnd) {
      var self = this;
      var list = this.detailEntries;
      if (!list || !list.length) {
        return [];
      }
      var ss = self.normalizeTimetableTimeForGrid(slotStart);
      var se = self.normalizeTimetableTimeForGrid(slotEnd);
      return list.filter(function (entry) {
        if (entry.day !== day) {
          return false;
        }
        var es = self.normalizeTimetableTimeForGrid(entry.start_time);
        var ee = self.normalizeTimetableTimeForGrid(entry.end_time);
        return es < se && ee > ss;
      });
    },
    isTimetableGridTimeSlotNow(slot) {
      if (!slot) {
        return false;
      }
      var nowM = this.clockNow.getHours() * 60 + this.clockNow.getMinutes();
      var sm = this.parseTimeToMinutes(slot.start);
      var em = this.parseTimeToMinutes(slot.end);
      if (sm < 0 || em < 0) {
        return false;
      }
      return nowM >= sm && nowM < em;
    },
    findNextSessionMeta(entries, searchFromWeekMinutes, order, todayName, strictGreater, nowWeekMinutesForDelta) {
      var bestScore = Infinity;
      var best = null;
      var wi;
      for (wi = 0; wi < entries.length; wi++) {
        var e = entries[wi];
        var di = order.indexOf(e.day);
        if (di < 0) {
          continue;
        }
        var sm = this.parseTimeToMinutes(e.start_time);
        if (sm < 0) {
          continue;
        }
        var k;
        for (k = 0; k < 2; k++) {
          var score = di * 1440 + sm + k * 10080;
          var take = strictGreater ? score > searchFromWeekMinutes : score >= searchFromWeekMinutes;
          if (take && score < bestScore) {
            bestScore = score;
            best = { entry: e, score: score };
          }
        }
      }
      if (!best) {
        return null;
      }
      var ne = best.entry;
      var sm2 = this.parseTimeToMinutes(ne.start_time);
      var whenLabel = ne.day === todayName
        ? 'Today · ' + this.formatTime12hFromMinutes(sm2)
        : ne.day + ' · ' + this.formatTime12hFromMinutes(sm2);
      var deltaMinutes = best.score - nowWeekMinutesForDelta;
      return { entry: ne, deltaMinutes: deltaMinutes, whenLabel: whenLabel };
    },
    minutesToTimetableHms(mins) {
      var cap = 24 * 60;
      var x = mins;
      if (x < 0) {
        x = 0;
      }
      if (x > cap) {
        x = cap;
      }
      var h = Math.floor(x / 60);
      var m = x % 60;
      var pad = function (n) {
        return n < 10 ? '0' + String(n) : String(n);
      };
      return pad(h) + ':' + pad(m) + ':00';
    },
    parseTimeToMinutes(str) {
      if (!str) {
        return -1;
      }
      var s = String(str).trim();
      var p = s.split(':');
      if (p.length < 2) {
        return -1;
      }
      var h = parseInt(p[0], 10);
      var m = parseInt(p[1], 10);
      if (isNaN(h) || isNaN(m)) {
        return -1;
      }
      return h * 60 + m;
    },
    formatTime12hFromMinutes(mins) {
      var d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setMinutes(mins);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    },
    formatDurationShort(minutes) {
      if (minutes <= 0) {
        return 'less than a minute';
      }
      if (minutes < 60) {
        return minutes + ' min';
      }
      var h = Math.floor(minutes / 60);
      var m = minutes % 60;
      if (m === 0) {
        return h + ' h';
      }
      return h + ' h ' + m + ' min';
    },
    formatDurationLong(minutes) {
      if (minutes <= 0) {
        return 'less than a minute';
      }
      if (minutes < 60) {
        return minutes + ' minutes';
      }
      if (minutes < 1440) {
        var h2 = Math.floor(minutes / 60);
        var m2 = minutes % 60;
        if (m2 === 0) {
          return h2 + (h2 === 1 ? ' hour' : ' hours');
        }
        return h2 + ' h ' + m2 + ' min';
      }
      var days = Math.floor(minutes / 1440);
      var rem = minutes % 1440;
      var hs = Math.floor(rem / 60);
      var dayPart = days + (days === 1 ? ' day' : ' days');
      if (hs === 0) {
        return dayPart;
      }
      return dayPart + ' ' + hs + ' h';
    }
  }
};
</script>

<style scoped>
/* Legacy Tailwind in this project: avoid arbitrary values ([]), slash opacity (bg-red-500/50), and ring-*. */
.faculty-timetable-view .ftv-insight-row {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@media (min-width: 1280px) {
  .faculty-timetable-view .ftv-insight-row {
    flex-direction: row;
    align-items: flex-start;
    gap: 1.25rem;
  }

  .faculty-timetable-view .ftv-clock-slot {
    flex: 0 0 33.333%;
    max-width: 22rem;
  }

  .faculty-timetable-view .ftv-insight-main {
    flex: 1;
    min-width: 0;
  }
}

.faculty-timetable-view .ftv-insight-placeholder {
  border-radius: 1rem;
  border: 1px dashed #e2e8f0;
  background-color: rgba(248, 250, 252, 0.95);
  padding: 1.5rem 1.75rem;
  text-align: center;
}

.faculty-timetable-view .ftv-max-w-copy {
  max-width: 28rem;
}

.faculty-timetable-view .ftv-card-now {
  border-radius: 1rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid rgba(167, 243, 208, 0.85);
  background: linear-gradient(to right, #ecfdf5, #ffffff, rgba(240, 253, 250, 0.92));
}

.faculty-timetable-view .ftv-card-next {
  border-radius: 1rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid rgba(165, 180, 252, 0.92);
  background: linear-gradient(to bottom right, #eef2ff, #ffffff, #f5f3ff);
}

.faculty-timetable-view .ftv-card-pad {
  padding: 1rem 1.25rem;
}

@media (min-width: 640px) {
  .faculty-timetable-view .ftv-card-pad {
    padding: 1.25rem 1.5rem;
  }
}

.faculty-timetable-view .ftv-gap-inner {
  gap: 1rem;
}

.faculty-timetable-view .ftv-icon-badge {
  display: flex;
  height: 3rem;
  width: 3rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.faculty-timetable-view .ftv-icon-badge-emerald {
  background-color: #059669;
  color: #fff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 0 0 4px #d1fae5;
}

.faculty-timetable-view .ftv-icon-badge-indigo {
  background-color: #4f46e5;
  color: #fff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 0 0 4px #e0e7ff;
}

.faculty-timetable-view .ftv-icon-badge-muted {
  height: 2.75rem;
  width: 2.75rem;
  background-color: #f1f5f9;
  color: #64748b;
  box-shadow: none;
}

.faculty-timetable-view .ftv-flex-min {
  min-width: 0;
  flex: 1 1 auto;
}

.faculty-timetable-view .ftv-badge-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.faculty-timetable-view .ftv-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #fff;
  padding: 0.125rem 0.625rem;
}

.faculty-timetable-view .ftv-badge-now {
  background-color: #059669;
}

.faculty-timetable-view .ftv-badge-next {
  background-color: #4f46e5;
}

.faculty-timetable-view .ftv-now-meta {
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 500;
  color: #065f46;
  opacity: 0.95;
}

.faculty-timetable-view .ftv-next-meta {
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 500;
  color: #312e81;
  opacity: 0.88;
}

.faculty-timetable-view .ftv-card-between {
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  padding: 1rem 1.25rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

@media (min-width: 640px) {
  .faculty-timetable-view .ftv-card-between {
    padding: 1.25rem 1.5rem;
  }
}

.faculty-timetable-view .ftv-sem-btn {
  padding: 0.625rem 1rem;
}

.faculty-timetable-view .ftv-sem-btn-active {
  box-shadow: 0 0 0 1px #bfdbfe;
}

.faculty-timetable-view .ftv-mt-tight {
  margin-top: 0.125rem;
}

.faculty-timetable-view .ftv-pill-y {
  padding-top: 0.125rem;
  padding-bottom: 0.125rem;
}

.faculty-timetable-view .ftv-icon-stack {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.faculty-timetable-view .ftv-th-border {
  border-right-color: rgba(59, 130, 246, 0.78);
}

.faculty-timetable-view .ftv-min-w-time {
  min-width: 8.5rem;
}

.faculty-timetable-view .ftv-th-sticky-shadow {
  box-shadow: 4px 0 8px -4px rgba(0, 0, 0, 0.12);
}

.faculty-timetable-view .ftv-th-day {
  min-width: 11rem;
}

.faculty-timetable-view thead th.ftv-th-day:last-of-type {
  border-right-width: 0;
}

.faculty-timetable-view .ftv-row-alt {
  background-color: rgba(248, 250, 252, 0.92);
}

.faculty-timetable-view .ftv-td-sticky-shadow {
  box-shadow: 4px 0 8px -4px rgba(0, 0, 0, 0.06);
}

.faculty-timetable-view .ftv-td-time-alt {
  background-color: rgba(241, 245, 249, 0.92);
}

.faculty-timetable-view .ftv-grid-cell {
  min-width: 11rem;
  max-width: 16rem;
}

.faculty-timetable-view tbody td.ftv-grid-cell:last-of-type {
  border-right-width: 0;
}

.faculty-timetable-view .ftv-grid-entry {
  background-color: #eff6ff;
  border: 1px solid rgba(191, 219, 254, 0.95);
  padding: 0.625rem;
  margin-bottom: 0.5rem;
}

.faculty-timetable-view .ftv-grid-entry:last-child {
  margin-bottom: 0;
}

.faculty-timetable-view .ftv-grid-entry:hover {
  background-color: rgba(219, 234, 254, 0.95);
}

.faculty-timetable-view .ftv-text-blue-dark {
  color: #1e3a8a;
}

.faculty-timetable-view .ftv-icon-tight-top {
  margin-top: 2px;
}

.faculty-timetable-view .ftv-shrink-0 {
  flex-shrink: 0;
}

.faculty-timetable-view .ftv-entry-time {
  font-size: 10px;
  margin-top: 0.375rem;
}

.faculty-timetable-view .ftv-details-bg {
  background-color: rgba(249, 250, 251, 0.95);
}

.faculty-timetable-view .ftv-list-hover:hover {
  background-color: rgba(248, 250, 252, 0.92);
}

/* “Now” highlights: cool blues to match header + ftv-grid-entry cards */
.faculty-timetable-view thead th.ftv-th-today {
  box-shadow: inset 0 -4px 0 rgba(125, 211, 252, 0.95);
  background-color: #1d4ed8;
}

.faculty-timetable-view tbody tr.ftv-tr-now {
  background-color: rgba(239, 246, 255, 0.97);
}

.faculty-timetable-view tbody tr.ftv-tr-now.ftv-row-alt {
  background-color: rgba(219, 234, 254, 0.92);
}

.faculty-timetable-view .ftv-td-time-now {
  background-color: rgba(219, 234, 254, 0.98);
  color: #1e3a8a;
  font-weight: 700;
}

.faculty-timetable-view .ftv-grid-cell-today {
  background-color: rgba(239, 246, 255, 0.55);
}

.faculty-timetable-view .ftv-grid-cell-now {
  background-color: rgba(191, 219, 254, 0.45);
  box-shadow: inset 0 0 0 2px rgba(59, 130, 246, 0.45);
}
</style>
